import React from "react";
import { RefundRequest, RefundRequestStatus } from "../../../common/types";
import { Panel, Header, FieldWithLabel, Button, Link } from "@glamcorner/components";
import "./refund-request-details-card.scss";
import { calcRequestTotal, formatDateTime, formatRefundRequestStatus } from "../../utils";
import { OrderIdLink } from "../OrderIdLink/OrderIdLink";
import { isRequestRefundable } from "../../../common/utils";
import { RefundInDays } from "../RefundInDays";
import { partiallyOrFullyRefunded } from "../../../server/models/RefundRequest/partiallyOrFullyRefunded";
import { VoidForm } from "./VoidForm";

interface RefundRequestDetailsCardProps {
  refundRequest: RefundRequest;
  inventoryUrl: string;
  onClickDelete: () => void;
  onClickVoid: (data: { reason: string; note: string }) => void;
  onRefundHandler: () => void;
  onDeleteFailedComponents: () => void;
}

export const RefundRequestDetailsCard: React.FC<RefundRequestDetailsCardProps> = ({
  refundRequest,
  inventoryUrl,
  onClickDelete,
  onClickVoid,
  onRefundHandler,
  onDeleteFailedComponents,
}) => {
  const magentoOrderUrl = `${inventoryUrl}/link/magento-order?order_id=${refundRequest.orderId}`;
  const isRefundable = isRequestRefundable(refundRequest.status);

  return (
    <Panel className="c-refund-request-details-card" ready={true}>
      <Header onClick={() => {}} withCloseButton={false}>
        Details
      </Header>
      <div className="c-refund-request-details-card__content">
        <FieldWithLabel label="Refund Request ID">{refundRequest.id}</FieldWithLabel>
        <FieldWithLabel label="Order ID">
          <OrderIdLink inventoryUrl={inventoryUrl} orderId={refundRequest.orderId} />
        </FieldWithLabel>
        <FieldWithLabel label="Customer Name">{refundRequest.customerName}</FieldWithLabel>
        <FieldWithLabel label="Customer Email">{refundRequest.customerEmail}</FieldWithLabel>
        <FieldWithLabel label="Refund Reason">{refundRequest.refundReason}</FieldWithLabel>
        <FieldWithLabel label="Refund Reason Detail">{refundRequest.refundReasonDetail || "-"}</FieldWithLabel>
        <FieldWithLabel label="Status">{formatRefundRequestStatus(refundRequest.status)}</FieldWithLabel>
        <FieldWithLabel label="Amount Total">{calcRequestTotal(refundRequest.components)}</FieldWithLabel>
        <FieldWithLabel label="Must refund in days">
          <RefundInDays refundRequest={refundRequest} />
        </FieldWithLabel>
        <FieldWithLabel label="Created At">{formatDateTime(refundRequest.createdAt)}</FieldWithLabel>
        <FieldWithLabel label="Updated At">{formatDateTime(refundRequest.updatedAt)}</FieldWithLabel>
        <FieldWithLabel label="Magento Link">
          <a href={magentoOrderUrl} className="t-order-id" target="_blank">
            Magento {refundRequest.orderId}
          </a>
        </FieldWithLabel>

        {isRefundable && !refundRequest.deletedAt && (
          <div className="c-refund-request-details-card__refund-btn-container">
            <Button
              onClick={onRefundHandler}
              buttonType="primary"
              text="Schedule Refund Now"
              className="c-refund-request-details-card__refund-btn"
            />
            <span className="c-refund-request-details-card__refund-caption">
              By pressing this button, this refund request will be scheduled immediately for refund instead of waiting
              for the next batch
            </span>
          </div>
        )}

        {partiallyOrFullyRefunded(refundRequest) ? (
          <p className="c-refund-request-details-card__cant-delete-notice">
            Can't delete or void a refund request that has been partially or fully refunded.
          </p>
        ) : (
          <>
            {refundRequest.voidedAt && (
              <>
                <FieldWithLabel label="Voided At">{formatDateTime(refundRequest.voidedAt)}</FieldWithLabel>
                <FieldWithLabel label="Void Reason">{refundRequest.voidReason}</FieldWithLabel>
                <FieldWithLabel label="Void Note">{refundRequest.voidNote}</FieldWithLabel>
              </>
            )}
            {!refundRequest.voidedAt && !refundRequest.deletedAt && <VoidForm onSubmit={onClickVoid} />}
            <br />
            <br />
            {refundRequest.deletedAt && (
              <FieldWithLabel label="Deleted At">{formatDateTime(refundRequest.deletedAt)}</FieldWithLabel>
            )}
            {!refundRequest.deletedAt && (
              <Link onClick={onClickDelete} text="Delete" className="c-refund-request-details-card__delete-link" />
            )}
          </>
        )}
        {refundRequest.status === RefundRequestStatus.PartiallyRefunded && (
          <>
            <br />
            <br />
            <Link
              onClick={onDeleteFailedComponents}
              text="Delete Failed Components"
              className="c-refund-request-details-card__delete-failed-components-link"
            />
          </>
        )}
      </div>
    </Panel>
  );
};
